import configData from '../../config.json';

const DEFAULT_TIMEOUT = 15000;

// Some backend endpoints are sensitive to the exact serialized form of
// specific query parameters. In particular, `original_url` must NOT be
// fully percent-encoded the same way URLSearchParams would encode it,
// otherwise the API returns "400 Invalid Parameters".
//
// We therefore keep a small allowlist of query params that should be
// serialized in a minimally encoded form.
const RAW_QUERY_PARAMS = new Set(['original_url']);

function resolveBaseUrl(path) {
  // If `path` is already absolute, use it as-is.
  if (/^https?:\/\//i.test(path)) {
    return new URL(path);
  }

  // Otherwise resolve it against the configured API base URL.
  // If the config value is relative, resolve it from the current origin.
  const base = configData.azureFunctionEndpoint || '/';
  const resolvedBase = /^https?:\/\//i.test(base)
    ? base
    : new URL(base, window.location.origin).toString();

  // Ensure the base ends with `/` so URL resolution behaves predictably.
  const normalizedBase = resolvedBase.endsWith('/')
    ? resolvedBase
    : `${resolvedBase}/`;

  // Remove a leading slash from the path so it appends to the base path
  // instead of replacing it.
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;

  return new URL(normalizedPath, normalizedBase);
}

function encodeQueryParam(key, value) {
  const encodedKey = encodeURIComponent(key);
  const stringValue = String(value);

  if (RAW_QUERY_PARAMS.has(key)) {
    // IMPORTANT:
    // The backend expects `original_url` in a near-raw form.
    //
    // Example of what the backend accepts:
    //   original_url=aHR0cHM6Ly9.../tag?rid=...=
    //
    // Example of what breaks it:
    //   original_url=aHR0cHM6Ly9.../tag%3Frid%3D...%3D
    //
    // So for these special params we preserve characters such as:
    //   ?, /, =
    // and only escape characters that would break the OUTER query string:
    //   & => would start a new query param
    //   # => would start the URL fragment
    const safeRawValue = stringValue.replace(/&/g, '%26').replace(/#/g, '%23');

    return `${encodedKey}=${safeRawValue}`;
  }

  // Default behavior for normal query parameters:
  // fully percent-encode the value.
  return `${encodedKey}=${encodeURIComponent(stringValue)}`;
}

function buildUrl(path, query = {}) {
  const url = resolveBaseUrl(path);

  // Build the query string manually instead of using URLSearchParams
  // because URLSearchParams would over-encode `original_url`.
  const pairs = Object.entries(query)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    )
    .map(([key, value]) => encodeQueryParam(key, value));

  if (pairs.length > 0) {
    // Preserve query params that may already be present in `path`.
    const existingSearch = url.search ? url.search.slice(1) : '';

    url.search = existingSearch
      ? `${existingSearch}&${pairs.join('&')}`
      : pairs.join('&');
  }

  return url.toString();
}

function isPlainObject(value) {
  if (!value || Object.prototype.toString.call(value) !== '[object Object]') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function isJsonSerializable(value) {
  return isPlainObject(value) || Array.isArray(value);
}

function hasHeaderValues(headers) {
  return [...headers.keys()].length > 0;
}

function buildHeaders({ headers = {}, token, hasJsonBody, isBodyAllowed }) {
  const finalHeaders = new Headers(headers);

  // Add auth header if a token is provided by the caller.
  //
  // NOTE:
  // This will trigger a CORS preflight for cross-origin requests.
  // Since the API requires authorization, that preflight is expected.
  if (token) {
    finalHeaders.set('Authorization', token);
  }

  // Only set Content-Type when we are actually sending a JSON body.
  //
  // This avoids unnecessary preflight triggers on GET/HEAD requests.
  if (hasJsonBody && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json');
  }

  // Bodyless requests should not carry a Content-Type header.
  // GET/HEAD requests do not have a request body, so Content-Type is not useful.
  if (!isBodyAllowed) {
    finalHeaders.delete('Content-Type');
  }

  // Avoid passing an empty Headers object to fetch.
  return hasHeaderValues(finalHeaders) ? finalHeaders : undefined;
}

function createRequestSignal(timeout, externalSignal) {
  const hasTimeout = Number.isFinite(timeout) && timeout > 0;

  if (!hasTimeout && !externalSignal) {
    return {
      signal: undefined,
      cleanup: () => {},
      didTimeout: () => false,
    };
  }

  if (!hasTimeout && externalSignal) {
    return {
      signal: externalSignal,
      cleanup: () => {},
      didTimeout: () => false,
    };
  }

  let timedOut = false;

  const timeoutController = new AbortController();

  const timeoutId = window.setTimeout(() => {
    timedOut = true;
    timeoutController.abort();
  }, timeout);

  if (!externalSignal) {
    return {
      signal: timeoutController.signal,
      cleanup: () => {
        clearTimeout(timeoutId);
      },
      didTimeout: () => timedOut,
    };
  }

  const combinedController = new AbortController();

  const abortFromExternal = () => {
    combinedController.abort(externalSignal.reason);
  };

  const abortFromTimeout = () => {
    combinedController.abort();
  };

  if (externalSignal.aborted) {
    abortFromExternal();
  } else {
    externalSignal.addEventListener('abort', abortFromExternal, { once: true });
  }

  timeoutController.signal.addEventListener('abort', abortFromTimeout, {
    once: true,
  });

  return {
    signal: combinedController.signal,
    cleanup: () => {
      clearTimeout(timeoutId);
      externalSignal.removeEventListener('abort', abortFromExternal);
      timeoutController.signal.removeEventListener('abort', abortFromTimeout);
    },
    didTimeout: () => timedOut,
  };
}

async function parseJsonSafely(response) {
  const text = await response.text();

  if (!text || !text.trim()) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function parseResponseBody(response, responseType) {
  // Allow callers to skip body parsing.
  if (responseType === 'raw') return null;

  // Empty responses should not be parsed as JSON/text.
  if (response.status === 204 || response.status === 205) {
    return null;
  }

  // Allow callers to force a specific response parsing mode.
  if (responseType === 'text') return response.text();
  if (responseType === 'blob') return response.blob();
  if (responseType === 'arrayBuffer') return response.arrayBuffer();

  // JSON mode should tolerate empty bodies.
  if (responseType === 'json') {
    return parseJsonSafely(response);
  }

  // Auto-detect JSON by Content-Type when responseType is "auto".
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return parseJsonSafely(response);
  }

  return response.text();
}

function createHttpError(response, data) {
  // Normalize HTTP failures into an Error object with useful metadata
  // for logging, debugging and UI handling.
  const error = new Error(`HTTP ${response.status} ${response.statusText}`);
  error.name = 'HttpError';
  error.status = response.status;
  error.statusText = response.statusText;
  error.headers = response.headers;
  error.data = data;
  error.correlationId = response.headers.get('x-correlation-id') || '';
  return error;
}

export async function request(path, options = {}) {
  const {
    method = 'GET',
    query,
    headers,
    body,
    token,
    timeout = DEFAULT_TIMEOUT,
    responseType = 'auto',
    signal,
  } = options;

  const upperMethod = method.toUpperCase();

  // GET and HEAD requests must not send a request body.
  // This keeps requests cleaner and avoids accidental preflight triggers.
  const isBodyAllowed = upperMethod !== 'GET' && upperMethod !== 'HEAD';

  let requestBody;
  const hasJsonBody = isBodyAllowed && isJsonSerializable(body);

  if (isBodyAllowed && body !== undefined && body !== null) {
    if (hasJsonBody) {
      requestBody = JSON.stringify(body);
    } else {
      requestBody = body;
    }
  }

  const finalHeaders = buildHeaders({
    headers,
    token,
    hasJsonBody,
    isBodyAllowed,
  });

  const requestSignal = createRequestSignal(timeout, signal);

  try {
    const fetchOptions = {
      method: upperMethod,
      signal: requestSignal.signal,
    };

    if (finalHeaders) {
      fetchOptions.headers = finalHeaders;
    }

    if (isBodyAllowed && requestBody !== undefined) {
      fetchOptions.body = requestBody;
    }

    const response = await fetch(buildUrl(path, query), fetchOptions);

    const data = await parseResponseBody(response, responseType);

    // Convert non-2xx responses into structured errors.
    if (!response.ok) {
      throw createHttpError(response, data);
    }

    return {
      data,
      status: response.status,
      headers: response.headers,
      correlationId: response.headers.get('x-correlation-id') || '',
    };
  } catch (error) {
    // Normalize aborts caused by our timeout into a clearer error type.
    if (requestSignal.didTimeout()) {
      const timeoutError = new Error('Request timeout');
      timeoutError.name = 'TimeoutError';
      throw timeoutError;
    }

    // If the caller's own signal aborted the request, keep the original error.
    throw error;
  } finally {
    requestSignal.cleanup();
  }
}

const httpClient = {
  request,

  get(path, options = {}) {
    return request(path, { ...options, method: 'GET' });
  },

  post(path, options = {}) {
    return request(path, { ...options, method: 'POST' });
  },

  put(path, options = {}) {
    return request(path, { ...options, method: 'PUT' });
  },

  patch(path, options = {}) {
    return request(path, { ...options, method: 'PATCH' });
  },

  delete(path, options = {}) {
    return request(path, { ...options, method: 'DELETE' });
  },
};

export default httpClient;
